import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { showDebugHintForAdmin } from '@/lib/videoPlayerDebug';

/**
 * Cache of in-flight/settled admin checks, keyed by access token.
 *
 * Five components call this hook, and each mount used to issue its own request
 * for an answer that cannot differ. Sharing the promise per token collapses
 * them into one call, and a token change (login/refresh) naturally re-checks.
 */
const adminCheckCache = new Map<string, Promise<boolean>>();

async function fetchIsSystemAdmin(accessToken: string): Promise<boolean> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL || 'https://lkwtufmxdmfcqhesovvn.supabase.co'}/functions/v1/is-system-admin`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '',
        },
      }
    );
    if (!response.ok) return false;
    const data = await response.json();
    return data.isAdmin === true;
  } catch {
    // A failed check must never grant admin.
    return false;
  }
}

/**
 * Hook to check if the current user is a system admin.
 * Returns false by default — never exposes admin status unless verified server-side.
 *
 * One request per session token, shared across all callers.
 */
export function useIsSystemAdmin(): boolean {
  const { session } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const token = session?.access_token;
    if (!token) {
      setIsAdmin(false);
      return;
    }

    let cancelled = false;
    let pending = adminCheckCache.get(token);
    if (!pending) {
      pending = fetchIsSystemAdmin(token);
      adminCheckCache.set(token, pending);
      // Don't cache a rejection — let the next mount retry.
      pending.catch(() => adminCheckCache.delete(token));
    }

    pending.then((result) => {
      if (cancelled) return;
      setIsAdmin(result);
      // The debug-toggle hint rides along on the answer we already have,
      // instead of videoPlayerDebug making its own duplicate request on import.
      if (result) showDebugHintForAdmin();
    });

    return () => { cancelled = true; };
  }, [session?.access_token]);

  return isAdmin;
}
